import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useSearchParams } from 'react-router';
import { useLogout, useSecondFactor, useSession } from '../../api/queries';
import { Alert, Button, Field, Input, Loading } from '../../components/ui';
import { errorMessage } from '../../i18n/labels';
import { AuthLayout } from './AuthLayout';
import { NextRedirect } from '../../app/guards';

/** Second step of sign-in when TOTP is enabled (FR-ACC-02). */
export function TwoFactorPage() {
  const { t } = useTranslation();
  const session = useSession();
  const [params] = useSearchParams();
  const verify = useSecondFactor();
  const logout = useLogout();
  const [useRecovery, setUseRecovery] = useState(false);
  const [value, setValue] = useState('');

  if (session.isPending) return <Loading />;
  if (session.data?.authenticated) return <NextRedirect next={params.get('next')} />;
  if (!(session.data && 'mfaPending' in session.data && session.data.mfaPending))
    return <Navigate to="/login" replace />;

  const valid = useRecovery ? value.trim().length >= 10 : /^\d{6}$/.test(value);
  return (
    <AuthLayout
      title={t('auth.twoFactorTitle')}
      subtitle={t(useRecovery ? 'auth.recoverySubtitle' : 'auth.twoFactorSubtitle')}
    >
      {verify.error && (
        <Alert tone="danger" className="mb-4">
          {errorMessage(t, verify.error)}
        </Alert>
      )}
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (!valid) return;
          // On success the session becomes signed in and NextRedirect above takes over.
          verify.mutate(useRecovery ? { recoveryCode: value.trim() } : { code: value });
        }}
      >
        <Field label={t(useRecovery ? 'auth.recoveryCode' : 'auth.authCode')}>
          {(p) =>
            useRecovery ? (
              <Input
                autoComplete="off"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                {...p}
              />
            ) : (
              <Input
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="\d{6}"
                maxLength={6}
                className="font-mono tracking-[0.4em]"
                value={value}
                onChange={(e) => setValue(e.target.value.replace(/\D/g, ''))}
                {...p}
              />
            )
          }
        </Field>
        <Button type="submit" className="w-full" disabled={!valid} loading={verify.isPending}>
          {t('auth.verify')}
        </Button>
      </form>
      <div className="mt-4 flex justify-between text-sm">
        <button
          type="button"
          className="link"
          onClick={() => {
            setUseRecovery((r) => !r);
            setValue('');
          }}
        >
          {t(useRecovery ? 'auth.useAuthenticator' : 'auth.useRecoveryCode')}
        </button>
        <Link to="/login" className="link" onClick={() => logout.mutate()}>
          {t('auth.cancel')}
        </Link>
      </div>
    </AuthLayout>
  );
}
